import React, { useState, useEffect } from 'react'
import Modal from 'components/modal'
import DeleteModal from 'components/delete'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import { IOrganisation, IOrganisationTeam, IOrganisationTeamDetail, ITeamsListResponse } from 'types'
import Input from 'components/input'
import UserSelect, { SelectedCount } from 'components/userSelect'

interface EditTeamModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  organisation: IOrganisation
  team: IOrganisationTeam
  teamsData?: ITeamsListResponse
  onUpdated: () => void
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({ open, setOpen, organisation, team, teamsData, onUpdated }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { user, refreshUser } = useAuth()
  const isSuperAdmin = user?.isSuperAdmin ?? false

  const [name, setName] = useState<string>(team.name)
  const [seatLimitInput, setSeatLimitInput] = useState<string>(String(team.seatLimit))
  const [selectedManagerEmails, setSelectedManagerEmails] = useState<string[]>(team.managers.map((m) => m.email))
  const [selectedMemberEmails, setSelectedMemberEmails] = useState<string[]>([])
  const [externalEmails, setExternalEmails] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState<string>('')
  const [externalUsersOpen, setExternalUsersOpen] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [teamDetail, setTeamDetail] = useState<IOrganisationTeamDetail | null>(null)

  useEffect(() => {
    if (!open) return
    setName(team.name)
    setSeatLimitInput(String(team.seatLimit))
    setSelectedManagerEmails(team.managers.map((m) => m.email))
    setSelectedMemberEmails([])
    setExternalEmails([])
    setEmailInput('')
    setExternalUsersOpen(false)
    setTeamDetail(null)

    api
      .getOrganisationTeamDetail(organisation.publicId, team.publicId)
      .then((response) => {
        setTeamDetail(response.data)
        const orgUserEmails = new Set(
          organisation.users.filter((u) => !u.isExternal && !u.isPending).map((u) => u.user.email)
        )
        const orgMemberEmails: string[] = []
        const extEmails: string[] = []
        for (const m of response.data.members) {
          if (orgUserEmails.has(m.email)) {
            orgMemberEmails.push(m.email)
          } else {
            extEmails.push(m.email)
          }
        }
        const pendingEmails = (response.data.pendingMembers ?? []).map((p) => p.email)
        setSelectedMemberEmails(orgMemberEmails)
        setExternalEmails([...extEmails, ...pendingEmails])
      })
      .catch(displayErrorMessage)
  }, [open, team.publicId])

  const isDefault = team.isDefault
  const seatLimit = parseInt(seatLimitInput) || 0
  const currentSeatsInUse = teamDetail?.seatsUsed ?? team.seatsUsed
  const minSeatLimit = currentSeatsInUse

  const computeOtherTeamsAllocated = () => {
    if (!teamsData) return 0
    return teamsData.allocatedSeats - team.seatLimit
  }
  const otherTeamsAllocated = computeOtherTeamsAllocated()

  const maxSeatLimit = isDefault
    ? Infinity
    : isSuperAdmin
      ? Infinity
      : organisation.multiYearContract
        ? Infinity
        : teamsData
          ? teamsData.totalSeats - otherTeamsAllocated
          : team.seatLimit

  const orgUsers = organisation.users.map((u) => ({
    publicId: u.user.email,
    name: u.user.name || u.user.email,
    email: u.user.email,
    createdAt: u.user.createdAt
  }))

  const toggleManager = (email: string) => {
    setSelectedManagerEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  const managerEmailsSet = new Set(selectedManagerEmails)

  const toggleMember = (email: string) => {
    setSelectedMemberEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  const addExternalEmail = () => {
    const emails = emailInput
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    if (emails.length === 0) return
    setExternalEmails((prev) => [...prev, ...emails.filter((e) => !prev.includes(e))])
    setEmailInput('')
  }

  const removeExternalEmail = (email: string) => {
    setExternalEmails((prev) => prev.filter((e) => e !== email))
  }

  const computeOriginalMemberEmails = () => {
    if (!teamDetail) return new Set<string>()
    return new Set(teamDetail.members.map((m) => m.email))
  }

  const originalMemberEmails = computeOriginalMemberEmails()
  const disabledMemberEmails = selectedManagerEmails.filter((email) => originalMemberEmails.has(email))

  const canSave =
    seatLimit >= minSeatLimit &&
    seatLimit <= maxSeatLimit &&
    (isDefault || (name.trim().length > 0 && selectedManagerEmails.length > 0))

  const handleSave = () => {
    if (!canSave) return
    setSaving(true)
    const memberEmails = [...selectedMemberEmails, ...externalEmails]
    const payload: Record<string, unknown> = { seatLimit, memberEmails }
    if (!isDefault) payload.name = name.trim()
    if (isDefault || selectedManagerEmails.length > 0) payload.managerEmails = selectedManagerEmails
    api
      .updateOrganisationTeam(organisation.publicId, team.publicId, payload)
      .then(() => {
        setSnackbarMessage(`Team "${name.trim()}" updated`)
        setOpen(false)
        onUpdated()
        refreshUser()
      })
      .catch(displayErrorMessage)
      .finally(() => setSaving(false))
  }

  // Every site belongs to a team, so a team's sites are moved to other teams before it can go
  const ownsSites = team.siteCount > 0

  const handleDelete = async () => {
    await api.deleteOrganisationTeam(organisation.publicId, team.publicId)
    setSnackbarMessage(`Team "${team.name}" deleted`)
    setOpen(false)
    onUpdated()
    refreshUser()
  }

  return (
    <>
      <Modal id="edit-team" title={`Edit Team — ${team.name}`} open={open} setOpen={setOpen}>
        {/* Team Name */}
        {!isDefault && (
          <div className="mb-4">
            <label htmlFor="edit-team-name" className="block text-sm font-semibold mb-1">
              Team Name
            </label>
            <Input id="edit-team-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        )}

        {/* Seat Limit */}
        {(!isDefault || isSuperAdmin) && (
          <div className="mb-4">
            <label htmlFor="edit-team-seat-limit" className="block text-sm font-semibold mb-1">
              Seat Limit
            </label>
            <Input
              id="edit-team-seat-limit"
              type="number"
              min={minSeatLimit}
              max={maxSeatLimit === Infinity ? undefined : maxSeatLimit}
              value={seatLimitInput}
              onChange={(e) => setSeatLimitInput(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
            <span className="text-xs text-muted">
              Min: {minSeatLimit} (current in-use)
              {maxSeatLimit !== Infinity && ` · Max: ${maxSeatLimit}`}
              {isSuperAdmin &&
                teamsData &&
                ` · Total across all teams: ${teamsData.totalSeats - team.seatLimit + seatLimit}`}
            </span>
            {seatLimit < minSeatLimit && (
              <span className="text-xs text-red block">Seat limit cannot be less than current usage</span>
            )}
          </div>
        )}

        {/* Team Managers */}
        <div className="mb-4">
          <span className="block text-sm font-semibold mb-1">
            Team Managers {!isDefault && '(minimum 1)'}
            <SelectedCount count={selectedManagerEmails.length} />
          </span>

          <UserSelect users={orgUsers} selectedEmails={selectedManagerEmails} onToggle={toggleManager} showToolbar />

          {!isDefault && selectedManagerEmails.length === 0 && (
            <span className="text-xs text-red block mb-1">At least one manager is required</span>
          )}
        </div>

        {/* Assigned users */}
        <div className="mb-5">
          <span className="block text-sm font-semibold mb-1">
            Assigned Users
            <SelectedCount count={selectedMemberEmails.length} />
          </span>
          <span className="text-xs text-muted block mb-1">Assigning users upgrades them to a paid license.</span>
          <UserSelect
            users={orgUsers}
            selectedEmails={selectedMemberEmails}
            disabledEmails={disabledMemberEmails}
            pinnedEmails={selectedManagerEmails}
            onToggle={toggleMember}
            placeholder="Search members..."
            showToolbar
            renderDetail={(user) =>
              managerEmailsSet.has(user.email) ? (
                <span className="text-xs text-muted italic">
                  {originalMemberEmails.has(user.email) ? 'Manager — cannot be removed' : 'Manager'}
                </span>
              ) : null
            }
          />
        </div>

        {/* External Users */}
        <div className="mb-5">
          <div
            role="button"
            tabIndex={0}
            className="!p-0 flex items-center gap-1 text-sm font-semibold mb-1 cursor-pointer"
            onClick={() => setExternalUsersOpen((prev) => !prev)}
          >
            <span className={`transition-transform ${externalUsersOpen ? 'rotate-90' : ''}`}>&#9654;</span>
            External Users
            {externalEmails.length > 0 && <small className="font-normal text-muted">({externalEmails.length})</small>}
          </div>
          {externalUsersOpen && (
            <>
              <span className="text-xs text-muted block mb-2">
                Add users outside of your organisation. External users consume a paid seat.
              </span>
              <div className="flex gap-2 mb-2">
                <Input
                  id="edit-team-external-email"
                  type="email"
                  placeholder="Enter email address..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addExternalEmail()
                    }
                  }}
                />
                <Button internalType="outline" onClick={addExternalEmail}>
                  Add
                </Button>
              </div>
              {externalEmails.length > 0 && (
                <UserSelect
                  users={externalEmails.map((email) => ({ publicId: email, name: email, email }))}
                  onRemove={removeExternalEmail}
                  hideSearch
                  renderDetail={(user) => {
                    const isPending = (teamDetail?.pendingMembers ?? []).some((p) => p.email === user.email)
                    return isPending ? (
                      <span className="text-xs text-orange bg-really-light-yellow px-1.5 py-0.5 rounded">Invited</span>
                    ) : (
                      <span className="text-xs text-blue bg-really-light-blue px-1.5 py-0.5 rounded">
                        External User
                      </span>
                    )
                  }}
                />
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isDefault && (
            <>
              <Button internalType="danger" onClick={() => setDeleteModalOpen(true)} disabled={ownsSites}>
                Delete This Team
              </Button>
              {ownsSites && (
                <span className="text-xs text-muted">
                  Move this team&apos;s {team.siteCount === 1 ? 'site' : `${team.siteCount} sites`} to other teams
                  before deleting it.
                </span>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button internalType="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button internalType="accept" onClick={handleSave} disabled={!canSave || saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteModal
        id="delete-team"
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        resourceName={team.name}
        deleteResource={handleDelete}
        otherContent={
          <p className="text-sm text-muted">
            All members will be moved to the Default team. This action cannot be undone.
          </p>
        }
      />
    </>
  )
}

export default EditTeamModal
